import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { toast } from "@project/hooks/use-toast";
import ReactMarkdown from "react-markdown";

interface AIResultDisplayProps {
  content: string;
}

const proseClasses =
  "prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-headings:font-display prose-h1:text-base prose-h2:text-sm prose-h3:text-[13px] prose-p:text-[13px] prose-p:leading-relaxed prose-li:text-[13px] prose-strong:text-foreground prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-h2:mt-4 prose-h2:mb-2 prose-h3:mt-3 prose-h3:mb-1 prose-p:my-1.5 prose-hr:my-3 prose-code:text-primary prose-code:bg-primary/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[12px] prose-a:text-primary prose-table:text-[12px] prose-th:text-[12px] prose-th:font-semibold prose-th:text-foreground prose-td:text-[12px] prose-th:border prose-th:border-border prose-th:px-2 prose-th:py-1.5 prose-td:border prose-td:border-border prose-td:px-2 prose-td:py-1.5 prose-table:border-collapse";

const AIResultDisplay = ({ content }: AIResultDisplayProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast({ title: "Copied!", description: "Content copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = content;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      toast({ title: "Copied!", description: "Content copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1.5 rounded-lg border-border text-[11px] hover:bg-muted"
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="h-3 w-3 text-[hsl(var(--status-active))]" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className={proseClasses}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </div>
  );
};

export default AIResultDisplay;
